import {AxiosInstance, AxiosResponse} from "axios";
import {Flowchart, FlowchartInstancePath, FlowchartNodePositionUpdate} from "@/core/interfaces/flowchart/flowchart";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";

/**
 * The Flowchart resource.
 * @param {Object} client - The injected client.
 */
export default function (client: AxiosInstance) {
    return {
        /**
         * Get all flowcharts.
         */
        async index(): Promise<Flowchart[]> {
            return await client.get('flowcharts')
                .then((response: AxiosResponse) => response.data.data)
                .catch(() => []);
        },
        /**
         * Show a flowchart.
         *
         * @param flowchart Either the ID or the slug of the flowchart.
         */
        async show(flowchart: number | string): Promise<Flowchart> {
            return await client.get(`flowcharts/${flowchart}`)
                .then((response: AxiosResponse) => response.data.data)
                .catch(() => []);
        },
        /**
         * Update the positions of the nodes of a flowchart. Restricted to admin users.
         *
         * @param flowchartId The flowchart ID.
         * @param nodes The nodes with their new positions.
         */
        async updateNodePositions(flowchartId: number, nodes: FlowchartNodePositionUpdate[]): Promise<void> {
            await client.patch(`flowcharts/${flowchartId}/nodes/positions`, {nodes});
        },
        /**
         * Print a flowchart.
         *
         * @param flowchartId  The flowchart ID.
         * @param consumer  The patient the flowchart is run for.
         * @param payload  The payload.
         * @param path  The path.
         */
        async print(flowchartId: number, consumer: Customer | Beneficiary, payload: object, path: FlowchartInstancePath): Promise<Blob> {
            return await client.post(
                `flowcharts/${flowchartId}/print`,
                {payload, consumer_key: consumer.consumer_key, path},
                {responseType: 'blob'}
            )
                .then((response: AxiosResponse) => response.data)
                .catch(() => new Blob());
        }
    };
}
